import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Loader2, X } from 'lucide-react';
import { AdminLayout } from './AdminDashboard';
import { API } from '../../App';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';

const AdminProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    category: 'Home',
    price: '',
    price_on_request: false,
    description: '',
    dimensions: '',
    material: '',
    image_url_1: '',
    image_url_2: '',
    image_url_3: ''
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API}/products`);
      const data = await response.json();
      setProducts(data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (product = null) => {
    if (product) {
      setEditingProduct(product);
      setFormData({
        name: product.name,
        category: product.category,
        price: product.price.toString(),
        price_on_request: product.price_on_request || false,
        description: product.description,
        dimensions: product.dimensions,
        material: product.material,
        image_url_1: (product.image_urls && product.image_urls[0]) ? product.image_urls[0] : '',
        image_url_2: (product.image_urls && product.image_urls[1]) ? product.image_urls[1] : '',
        image_url_3: (product.image_urls && product.image_urls[2]) ? product.image_urls[2] : ''
      });
    } else {
      setEditingProduct(null);
      setFormData({
        name: '',
        category: 'Home',
        price: '',
        price_on_request: false,
        description: '',
        dimensions: '',
        material: '',
        image_url_1: '',
        image_url_2: '',
        image_url_3: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCategoryChange = (value) => {
    setFormData(prev => ({ ...prev, category: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const image_urls = [formData.image_url_1, formData.image_url_2, formData.image_url_3].filter(url => url.trim() !== '');
      const payload = {
        name: formData.name,
        category: formData.category,
        price: formData.price_on_request ? 0 : parseFloat(formData.price),
        price_on_request: formData.price_on_request,
        description: formData.description,
        dimensions: formData.dimensions,
        material: formData.material,
        image_urls
      };

      const url = editingProduct 
        ? `${API}/admin/products/${editingProduct.id}`
        : `${API}/admin/products`;
      
      const method = editingProduct ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save product');
      }

      toast.success(editingProduct ? 'Product updated!' : 'Product created!');
      handleCloseModal();
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(error.message || 'Failed to save product');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/admin/products/${productId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete product');
      }

      toast.success('Product deleted!');
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(error.message || 'Failed to delete product');
    }
  };

  return (
    <AdminLayout activeTab="products">
      <div data-testid="admin-products">
        <div className="flex justify-between items-center mb-8">
          <h1 className="font-heading text-2xl font-semibold text-[#1A1A1A]">Products</h1>
          <button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center text-sm"
            data-testid="add-product-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Add Product
          </button>
        </div>

        {loading ? (
          <div className="bg-white p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#C4A882]" />
          </div>
        ) : products.length === 0 ? (
          <div className="bg-white p-8 text-center">
            <p className="font-body text-[#666666] mb-4">No products yet</p>
            <button 
              onClick={() => handleOpenModal()}
              className="btn-primary text-sm"
            >
              Add Your First Product
            </button>
          </div>
        ) : (
          <div className="bg-white overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#F9F9F8]">
                <tr>
                  <th className="px-6 py-3 text-left font-body text-xs uppercase tracking-wider text-[#666666]">Product</th>
                  <th className="px-6 py-3 text-left font-body text-xs uppercase tracking-wider text-[#666666]">Category</th>
                  <th className="px-6 py-3 text-left font-body text-xs uppercase tracking-wider text-[#666666]">Price</th>
                  <th className="px-6 py-3 text-right font-body text-xs uppercase tracking-wider text-[#666666]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product) => (
                  <tr key={product.id} className="border-b border-[#E5E5E5]" data-testid={`product-row-${product.id}`}>
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <img 
                          src={(product.image_urls && product.image_urls.length > 0) ? product.image_urls[0] : ''} 
                          alt={product.name}
                          className="w-12 h-12 object-cover mr-4"
                        />
                        <div>
                          <p className="font-body text-sm font-medium text-[#1A1A1A]">{product.name}</p>
                          <p className="font-body text-xs text-[#666666] line-clamp-1 max-w-xs">{product.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-block px-2 py-1 text-xs bg-[#F9F9F8] text-[#666666]">
                        {product.category}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <p className="font-heading text-sm font-semibold text-[#1A1A1A]">
                        {product.price_on_request ? 'Price on Request' : `₹${product.price.toLocaleString('en-IN')}`}
                      </p>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button 
                        onClick={() => handleOpenModal(product)}
                        className="p-2 text-[#666666] hover:text-[#C4A882] transition-colors"
                        data-testid={`edit-product-${product.id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button 
                        onClick={() => handleDelete(product.id)}
                        className="p-2 text-[#666666] hover:text-red-500 transition-colors ml-2"
                        data-testid={`delete-product-${product.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Product Modal */}
        <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {editingProduct ? 'Edit Product' : 'Add New Product'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name" className="font-body text-sm">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="mt-1 rounded-none"
                    required
                    data-testid="product-input-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="category" className="font-body text-sm">Category *</Label>
                  <Select value={formData.category} onValueChange={handleCategoryChange}>
                    <SelectTrigger className="mt-1 rounded-none" data-testid="product-input-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Home">Home Furniture</SelectItem>
                      <SelectItem value="Office">Office Furniture</SelectItem>
                      <SelectItem value="Custom">Custom & Modular</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="price" className="font-body text-sm">Price (₹) *</Label>
                  <Input
                    id="price"
                    name="price"
                    type="number"
                    value={formData.price_on_request ? '' : formData.price}
                    onChange={handleChange}
                    className="mt-1 rounded-none"
                    required={!formData.price_on_request}
                    min="0"
                    disabled={formData.price_on_request}
                    placeholder={formData.price_on_request ? 'Price on Request' : ''}
                    data-testid="product-input-price"
                  />
                  <div className="flex items-center gap-2 mt-2">
                    <input
                      type="checkbox"
                      id="price_on_request"
                      checked={formData.price_on_request}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_on_request: e.target.checked, price: e.target.checked ? '0' : prev.price }))}
                      className="w-4 h-4 accent-[#C4A882]"
                    />
                    <label htmlFor="price_on_request" className="font-body text-sm text-[#666666] cursor-pointer">
                      Price on Request
                    </label>
                  </div>
                </div>
                
                <div className="col-span-2">
                  <Label htmlFor="description" className="font-body text-sm">Description *</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    className="mt-1 rounded-none resize-none"
                    rows={3}
                    required
                    data-testid="product-input-description"
                  />
                </div>
                
                <div>
                  <Label htmlFor="dimensions" className="font-body text-sm">Dimensions *</Label>
                  <Input
                    id="dimensions"
                    name="dimensions"
                    value={formData.dimensions}
                    onChange={handleChange}
                    placeholder="L: 200cm x W: 90cm x H: 75cm"
                    className="mt-1 rounded-none"
                    required
                    data-testid="product-input-dimensions"
                  />
                </div>
                
                <div>
                  <Label htmlFor="material" className="font-body text-sm">Material *</Label>
                  <Input
                    id="material"
                    name="material"
                    value={formData.material}
                    onChange={handleChange}
                    placeholder="Solid Wood, Fabric"
                    className="mt-1 rounded-none"
                    required
                    data-testid="product-input-material"
                  />
                </div>
                
                <div className="col-span-2">
                  <Label className="font-body text-sm">Image URL 1 (Main) *</Label>
                  <Input
                    name="image_url_1"
                    value={formData.image_url_1}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="mt-1 rounded-none"
                    required
                  />
                  <Label className="font-body text-sm mt-2 block">Image URL 2</Label>
                  <Input
                    name="image_url_2"
                    value={formData.image_url_2}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="mt-1 rounded-none"
                  />
                  <Label className="font-body text-sm mt-2 block">Image URL 3</Label>
                  <Input
                    name="image_url_3"
                    value={formData.image_url_3}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="mt-1 rounded-none"
                  />
                  {formData.image_url_1 && (
                    <img 
                      src={formData.image_url_1} 
                      alt="Preview"
                      className="mt-2 h-24 object-cover"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  )}
                </div>
              </div>
              
              <div className="flex justify-end gap-4 pt-4">
                <button 
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary text-sm"
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  disabled={submitting}
                  className="btn-primary flex items-center text-sm disabled:opacity-50"
                  data-testid="product-submit-btn"
                >
                  {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {editingProduct ? 'Update Product' : 'Add Product'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
